import React, { useEffect, useState } from 'react';
import { Button, Form, Icon } from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, getLogo, getSystemName, showError, showInfo, showSuccess } from '../helpers';
import Turnstile from 'react-turnstile';
import SinoTokenBackground from './SinoTokenBackground';
import './LoginForm.css';
import './RegisterForm.css';

const RegisterForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logo = getLogo();
  const systemName = getSystemName();
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    password2: '',
    email: '',
    verification_code: '',
  });
  const { username, password, password2, email, verification_code } = inputs;
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  let affCode = new URLSearchParams(window.location.search).get('aff');

  if (affCode) {
    localStorage.setItem('aff', affCode);
  }

  useEffect(() => {
    document.body.classList.add('sino-login-shell');
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      setShowEmailVerification(status.email_verification);
      if (status.turnstile_check) {
        setTurnstileEnabled(true);
        setTurnstileSiteKey(status.turnstile_site_key);
      }
    }
    return () => {
      document.body.classList.remove('sino-login-shell');
    };
  }, []);

  useEffect(() => {
    let countdownInterval = null;
    if (disableButton && countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown((seconds) => seconds - 1);
      }, 1000);
    } else if (countdown === 0) {
      setDisableButton(false);
      setCountdown(30);
    }
    return () => clearInterval(countdownInterval);
  }, [disableButton, countdown]);

  function handleChange(e) {
    const { name, value } = e.target;
    setInputs((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }
    if (password.length < 8) {
      showInfo(t('messages.error.password_length'));
      return;
    }
    if (password !== password2) {
      showInfo(t('messages.error.password_mismatch'));
      return;
    }
    if (username && password) {
      if (turnstileEnabled && turnstileToken === '') {
        showInfo(t('messages.error.turnstile_wait'));
        return;
      }
      setLoading(true);
      if (!affCode) {
        affCode = localStorage.getItem('aff');
      }
      const payload = {
        ...inputs,
        aff_code: affCode,
      };
      const res = await API.post(
        `/api/user/register?turnstile=${turnstileToken}`,
        payload
      );
      const { success, message } = res.data;
      if (success) {
        navigate('/login');
        showSuccess(t('messages.success.register'));
      } else {
        showError(message);
      }
      setLoading(false);
    }
  }

  const sendVerificationCode = async () => {
    if (inputs.email === '') return;
    if (turnstileEnabled && turnstileToken === '') {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }
    setDisableButton(true);
    setLoading(true);
    const res = await API.get(
      `/api/verification?email=${inputs.email}&turnstile=${turnstileToken}`
    );
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('messages.success.verification_code'));
    } else {
      showError(message);
      setDisableButton(false);
      setCountdown(30);
    }
    setLoading(false);
  };

  return (
    <main className='sino-login-page sino-register-page'>
      <SinoTokenBackground />
      <section className='sino-login-art'>
        <header className='sino-login-brand'>
          <span className='sino-login-brand-mark'>
            <img src={logo} alt={systemName} />
          </span>
        </header>

        <nav className='sino-login-policy'>
          <Link to='/about'>隐私政策</Link>
          <Link to='/about'>服务条款</Link>
        </nav>
      </section>

      <section className='sino-login-panel sino-register-panel'>
        <div className='sino-login-card sino-register-card'>
          <h1>创建账号</h1>
          <p>注册后即可进入控制台管理 API 令牌与模型体验</p>

          <Form className='sino-login-form sino-register-form' onSubmit={handleSubmit}>
            <label htmlFor='register-username'>手机号</label>
            <input
              id='register-username'
              name='username'
              placeholder='13800138000'
              value={username}
              onChange={handleChange}
            />

            <label htmlFor='register-password'>密码</label>
            <div className='sino-password-field'>
              <input
                id='register-password'
                name='password'
                placeholder='至少 8 位字符'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handleChange}
              />
              <button
                type='button'
                aria-label='显示或隐藏密码'
                onClick={() => setShowPassword((visible) => !visible)}
              >
                <Icon name={showPassword ? 'eye slash outline' : 'eye'} />
              </button>
            </div>

            <label htmlFor='register-password2'>确认密码</label>
            <div className='sino-password-field'>
              <input
                id='register-password2'
                name='password2'
                placeholder='再次输入密码'
                type={showConfirmPassword ? 'text' : 'password'}
                value={password2}
                onChange={handleChange}
              />
              <button
                type='button'
                aria-label='显示或隐藏确认密码'
                onClick={() => setShowConfirmPassword((visible) => !visible)}
              >
                <Icon name={showConfirmPassword ? 'eye slash outline' : 'eye'} />
              </button>
            </div>

            {showEmailVerification && (
              <>
                <label htmlFor='register-email'>邮箱</label>
                <div className='sino-register-code-field'>
                  <input
                    id='register-email'
                    name='email'
                    placeholder={t('auth.register.email')}
                    value={email}
                    type='email'
                    onChange={handleChange}
                  />
                  <button
                    type='button'
                    disabled={loading || disableButton}
                    onClick={sendVerificationCode}
                  >
                    {disableButton
                      ? t('auth.register.get_code_retry', { countdown })
                      : t('auth.register.get_code')}
                  </button>
                </div>

                <label htmlFor='register-verification-code'>验证码</label>
                <input
                  id='register-verification-code'
                  name='verification_code'
                  placeholder={t('auth.register.verification_code')}
                  value={verification_code}
                  onChange={handleChange}
                />
              </>
            )}

            {turnstileEnabled && (
              <div className='sino-register-turnstile'>
                <Turnstile
                  sitekey={turnstileSiteKey}
                  onVerify={(token) => {
                    setTurnstileToken(token);
                  }}
                />
              </div>
            )}

            <Button
              fluid
              type='submit'
              className='sino-login-submit sino-register-submit'
              loading={loading}
            >
              {t('auth.register.button')}
            </Button>
          </Form>

          <p className='sino-login-register sino-register-login'>
            {t('auth.register.has_account')}
            <Link to='/login'>{t('auth.register.login')}</Link>
          </p>
        </div>
      </section>
    </main>
  );
};

export default RegisterForm;
